import React, { Suspense, lazy, useCallback, useState } from "react";
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { TiposDocsContextProvider } from './context/TipoDocumentoContext';
import { SnackBarContextProvider } from './context/SnackBarContext';
import { EstadoIdContextProvider } from "./context/EstadoIdContext";
import { CidadeIdContextProvider } from "./context/CidadeIdContext";
import { FilialContextProvider } from "./context/FilialContext";
import { PacienteContextProvider } from "./context/PacienteContext";
import { ProfContextProvider } from "./context/ProfContext";
import { EspecContextProvider } from "./context/EspecContext";
import { NavigateContextProvider } from "./context/NavigateContext";
import { UsuarioLogadoContextProvider } from "./context/UsuarioLogadoContext";
import { TipoConsultaContextProvider } from "./context/TipoConsultaContext";
import { PlanoSaudeContextProvider } from "./context/PlanoSaudeContext";
import { TabelaPrecoContextProvider } from "./context/TabelaPrecoContext";
import { TipoPagtoProfContextProvider } from "./context/TipoPagtoProfContext";
import { FormaAtendProfContextProvider } from "./context/FormaAtendProfContext";
import { AuthProvider, useAuth } from './context/AuthContext';
import SyncAuthContexts from './context/SyncAuthContexts';

import './App.css';
import Login from './pages/auth/Login';
import PrivateRoute from './components/auth/PrivateRoute';
import AuthBoot from './components/auth/AuthBoot';
import AvisoSessaoTrocada from './components/auth/AvisoSessaoTrocada';
import AvisoConcorrencia from './components/auth/AvisoConcorrencia';
import AvisoCadastroIncompleto from './components/auth/AvisoCadastroIncompleto';
import FaixaEmpresaTeste from './components/auth/FaixaEmpresaTeste';
import ConfiguracaoSomenteDesktop from './components/layout/ConfiguracaoSomenteDesktop';
import BarraNavegacaoInferior from './components/layout/BarraNavegacaoInferior';
import GuardaPerfil from './components/auth/GuardaPerfil';

// Telas carregadas sob demanda (padrão P-Q1, src/docs/padroes-front.md): o celular baixa só a tela aberta
const PaineisHome = lazy(() => import('./pages/paineis/PaineisHome'));
const EstadosIndex = lazy(() => import('./pages/estados/EstadosIndex'));
const EstadosForm = lazy(() => import('./pages/estados/EstadosForm'));
const CidadesIndex = lazy(() => import('./pages/cidades/CidadesIndex'));
const CidadesForm = lazy(() => import('./pages/cidades/CidadesForm'));
const PacientesForm = lazy(() => import('./pages/pacientes/PacientesForm'));
const PacientesIndex = lazy(() => import('./pages/pacientes/PacientesIndex'));
const PacienteTelefonesTela = lazy(() => import('./pages/relationships/pacientesFones/PacienteTelefonesTela'));
const ProfsForm = lazy(() => import('./pages/profissionais/ProfForm'));
const ProfsIndex = lazy(() => import('./pages/profissionais/ProfIndex'));
const EspecForm = lazy(() => import('./pages/espec/EspecForm'));
const EspecIndex = lazy(() => import('./pages/espec/EspecIndex'));
const ProfEspecIndex_CheckBox = lazy(() => import('./pages/relationships/profEspecializacoes/ProfEspecIndex_CheckBox'));
const ProfEspecsForm = lazy(() => import('./pages/relationships/profEspecializacoes/ProfEspecsForm'));
const PlanosSaudeIndex = lazy(() => import('./pages/planosSaude/PlanosSaudeIndex'));
const PlanosSaudeForm = lazy(() => import('./pages/planosSaude/PlanosSaudeForm'));
const TabelasPrecosIndex = lazy(() => import('./pages/tabelasPrecos/TabelasPrecosIndex'));
const FormasRecebimentoTela = lazy(() => import('./pages/formasRecebimento/FormasRecebimentoTela'));
const CondicoesRecebimentoTela = lazy(() => import('./pages/condicoesRecebimento/CondicoesRecebimentoTela'));
const TiposPagtosProfIndex = lazy(() => import('./pages/tiposPagtosProf/TiposPagtosProfIndex'));
const TiposPagtosProfForm = lazy(() => import('./pages/tiposPagtosProf/TiposPagtosProfForm'));
const TiposConsultasIndex = lazy(() => import('./pages/tiposConsultas/TiposConsultasIndex'));
const TiposConsultasForm = lazy(() => import('./pages/tiposConsultas/TiposConsultasForm'));
const DisponibilidadeSemanalGrade = lazy(() => import('./pages/disponibilidadeSemanal/DisponibilidadeSemanalGrade'));
const TiposPagtosProfissionaisTela = lazy(() => import('./pages/tiposPagtosProfissionais/TiposPagtosProfissionaisTela'));
const ProfEspecValoresTela = lazy(() => import('./pages/profEspecValores/ProfEspecValoresTela'));
const CarteirinhasPendentesTela = lazy(() => import('./pages/relationships/pacientesPacotes/CarteirinhasPendentesTela'));
const PacientesPacotesTela = lazy(() => import('./pages/relationships/pacientesPacotes/PacientesPacotesTela'));
const PacienteEvents = lazy(() => import('./pages/pacientes/PacienteEvents'));
const AgendaTela = lazy(() => import('./pages/agenda/AgendaTela'));
const HojeTela = lazy(() => import('./pages/atendimento/HojeTela'));
const AtendimentoTela = lazy(() => import('./pages/atendimento/AtendimentoTela'));
const ProntuarioTela = lazy(() => import('./pages/prontuario/ProntuarioTela'));
const LogAcessoTela = lazy(() => import('./pages/logAcesso/LogAcessoTela'));
const RelatorioAgendasPlanoTela = lazy(() => import('./pages/relatorios/RelatorioAgendasPlanoTela'));
const RelatorioFinanceiroTela = lazy(() => import('./pages/relatorios/RelatorioFinanceiroTela'));
const RelatorioRenovacaoPacotesTela = lazy(() => import('./pages/relatorios/RelatorioRenovacaoPacotesTela'));
const RelatorioSaldoSemAgendaTela = lazy(() => import('./pages/relatorios/RelatorioSaldoSemAgendaTela'));
const TitulosPlanoTela = lazy(() => import('./pages/titulosPlano/TitulosPlanoTela'));
const FaturasPlanoTela = lazy(() => import('./pages/faturasPlano/FaturasPlanoTela'));
const ContasReceberTela = lazy(() => import('./pages/contasReceber/ContasReceberTela'));
const NotasPlanoTela = lazy(() => import('./pages/notasPlano/NotasPlanoTela'));
const MotivosGlosaTela = lazy(() => import('./pages/motivosGlosa/MotivosGlosaTela'));
const UsuariosPerfisTela = lazy(() => import('./pages/usuarios/UsuariosPerfisTela'));
const PerfisPermissoesTela = lazy(() => import('./pages/perfis/PerfisPermissoesTela'));
const ImportacaoPlanilhasTela = lazy(() => import('./pages/importacoes/ImportacaoPlanilhasTela'));
const ParametrosSistemaTela = lazy(() => import('./pages/parametros/ParametrosSistemaTela'));
const SemPermissao = lazy(() => import('./pages/auth/SemPermissao'));
const DefinirSenha = lazy(() => import('./pages/auth/DefinirSenha'));
const MinhaConta = lazy(() => import('./pages/auth/MinhaConta'));

/** Enquanto a tela é baixada (rotas sob demanda). */
const CarregandoTela = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }} aria-busy="true" aria-label="Carregando tela">
    <CircularProgress aria-label="Carregando" />
  </Box>
);

/**
 * Árvore de domínio remonta ao trocar empresa/filial (limpa grids/caches).
 */
export function DominioProviders({ children, chaveTenant }) {
  return (
    <TiposDocsContextProvider key={chaveTenant}>
      <EstadoIdContextProvider>
        <CidadeIdContextProvider>
          <FilialContextProvider>
            <UsuarioLogadoContextProvider>
              <PacienteContextProvider>
                <ProfContextProvider>
                  <EspecContextProvider>
                    <TipoConsultaContextProvider>
                      <PlanoSaudeContextProvider>
                        <TabelaPrecoContextProvider>
                          <FormaAtendProfContextProvider>
                            <TipoPagtoProfContextProvider>
                              <NavigateContextProvider>
                                <SyncAuthContexts />
                                {children}
                              </NavigateContextProvider>
                            </TipoPagtoProfContextProvider>
                          </FormaAtendProfContextProvider>
                        </TabelaPrecoContextProvider>
                      </PlanoSaudeContextProvider>
                    </TipoConsultaContextProvider>
                  </EspecContextProvider>
                </ProfContextProvider>
              </PacienteContextProvider>
            </UsuarioLogadoContextProvider>
          </FilialContextProvider>
        </CidadeIdContextProvider>
      </EstadoIdContextProvider>
    </TiposDocsContextProvider>
  );
}

function RotasAutenticadas() {
  const { empresaId, filialId } = useAuth();
  const chaveTenant = `${empresaId || 'sem-empresa'}-${filialId || 'sem-filial'}`;

  return (
    <DominioProviders chaveTenant={chaveTenant}>
      <GuardaPerfil>
      <Suspense fallback={<CarregandoTela />}>
      <Routes>
        <Route path="/" element={<PaineisHome />} />
        <Route path="/estados" element={<ConfiguracaoSomenteDesktop titulo="Estados"><EstadosIndex /></ConfiguracaoSomenteDesktop>} />
        <Route path="/estados/create" element={<ConfiguracaoSomenteDesktop titulo="Estados"><EstadosForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/estados/:idEstado" element={<ConfiguracaoSomenteDesktop titulo="Estados"><EstadosForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/cidades" element={<ConfiguracaoSomenteDesktop titulo="Cidades"><CidadesIndex /></ConfiguracaoSomenteDesktop>} />
        <Route path="/cidades/create" element={<ConfiguracaoSomenteDesktop titulo="Cidades"><CidadesForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/cidades/:idCidade" element={<ConfiguracaoSomenteDesktop titulo="Cidades"><CidadesForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/pacientes" element={<PacientesIndex />} />
        <Route path="/pacientes/create" element={<PacientesForm />} />
        <Route path="/pacientes/:idPaciente" element={<PacientesForm />} />
        <Route path="/pacienteEvents/:idPaciente" element={<PacienteEvents />} />
        <Route path="/pacienteIdFonePac/:idPaciente" element={<PacienteTelefonesTela />} />
        <Route path="/pacientesPacotes/:idPaciente" element={<PacientesPacotesTela />} />
        <Route path="/carteirinhasPendentes" element={<CarteirinhasPendentesTela />} />
        <Route path="/profissionais" element={<ConfiguracaoSomenteDesktop titulo="Profissionais"><ProfsIndex /></ConfiguracaoSomenteDesktop>} />
        <Route path="/profissionais/create" element={<ConfiguracaoSomenteDesktop titulo="Profissionais"><ProfsForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/profissionais/:idProf" element={<ConfiguracaoSomenteDesktop titulo="Profissionais"><ProfsForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/profissionaisEspecIndex/:idProf" element={<ConfiguracaoSomenteDesktop titulo="Especializações do profissional"><ProfEspecIndex_CheckBox /></ConfiguracaoSomenteDesktop>} />
        <Route path="/profissionaisEspecForm/:idRelac" element={<ConfiguracaoSomenteDesktop titulo="Especializações do profissional"><ProfEspecsForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/profissionaisEspecForm" element={<ConfiguracaoSomenteDesktop titulo="Especializações do profissional"><ProfEspecsForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/especializacoes" element={<ConfiguracaoSomenteDesktop titulo="Especializações"><EspecIndex /></ConfiguracaoSomenteDesktop>} />
        <Route path="/especializacoes/create" element={<ConfiguracaoSomenteDesktop titulo="Especializações"><EspecForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/especializacoes/:idEspec" element={<ConfiguracaoSomenteDesktop titulo="Especializações"><EspecForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/tiposConsultas" element={<ConfiguracaoSomenteDesktop titulo="Tipos de consulta"><TiposConsultasIndex /></ConfiguracaoSomenteDesktop>} />
        <Route path="/tiposConsultas/create" element={<ConfiguracaoSomenteDesktop titulo="Tipos de consulta"><TiposConsultasForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/tiposConsultas/:idTipoCons" element={<ConfiguracaoSomenteDesktop titulo="Tipos de consulta"><TiposConsultasForm /></ConfiguracaoSomenteDesktop>} />
        {/* <Route path="/formasAtendProf" element={<FormasAtendProfIndex />} /> */}
        {/* <Route path="/formasAtendProf/create" element={<FormasAtendProfForm />} /> */}
        {/* <Route path="/formasAtendProf/:idFormaAtend" element={<FormasAtendProfForm />} /> */}
        <Route path="/tiposPagtosProf" element={<ConfiguracaoSomenteDesktop titulo="Tipos de pagamento"><TiposPagtosProfIndex /></ConfiguracaoSomenteDesktop>} />
        <Route path="/tiposPagtosProf/create" element={<ConfiguracaoSomenteDesktop titulo="Tipos de pagamento"><TiposPagtosProfForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/tiposPagtosProf/:idTipoPagto" element={<ConfiguracaoSomenteDesktop titulo="Tipos de pagamento"><TiposPagtosProfForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/planosSaude" element={<ConfiguracaoSomenteDesktop titulo="Planos de saúde"><PlanosSaudeIndex /></ConfiguracaoSomenteDesktop>} />
        <Route path="/planosSaude/create" element={<ConfiguracaoSomenteDesktop titulo="Planos de saúde"><PlanosSaudeForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/planosSaude/:idPlanoSaude" element={<ConfiguracaoSomenteDesktop titulo="Planos de saúde"><PlanosSaudeForm /></ConfiguracaoSomenteDesktop>} />
        <Route path="/formasRecebimento" element={<ConfiguracaoSomenteDesktop titulo="Formas de recebimento"><FormasRecebimentoTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/condicoesRecebimento" element={<ConfiguracaoSomenteDesktop titulo="Condições de recebimento"><CondicoesRecebimentoTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/tabelasPrecos" element={<TabelasPrecosIndex />} />
        <Route path="/tabelasPrecos/:idTabelaPreco" element={<TabelasPrecosIndex />} />
        <Route path="/disponibilidadeSemanal/:idProf" element={<ConfiguracaoSomenteDesktop titulo="Disponibilidade semanal"><DisponibilidadeSemanalGrade /></ConfiguracaoSomenteDesktop>} />
        <Route path="/profTiposPagtos/:idProf" element={<ConfiguracaoSomenteDesktop titulo="Tipos de pagamento do profissional"><TiposPagtosProfissionaisTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/profEspecValores/:idProf" element={<ConfiguracaoSomenteDesktop titulo="Valores por especialidade"><ProfEspecValoresTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/agenda" element={<AgendaTela />} />
        <Route path="/hoje" element={<HojeTela />} />
        <Route path="/atendimento/:agendaId" element={<AtendimentoTela />} />
        <Route path="/prontuario/:pacienteId" element={<ProntuarioTela />} />
        <Route path="/log-acesso" element={<LogAcessoTela />} />
        <Route path="/relatorios/agendas-por-plano" element={<RelatorioAgendasPlanoTela />} />
        <Route path="/relatorios/financeiro/a-receber" element={<ConfiguracaoSomenteDesktop titulo="Títulos a receber"><RelatorioFinanceiroTela tipo="a-receber" /></ConfiguracaoSomenteDesktop>} />
        <Route path="/relatorios/financeiro/baixados" element={<ConfiguracaoSomenteDesktop titulo="Títulos baixados"><RelatorioFinanceiroTela tipo="baixados" /></ConfiguracaoSomenteDesktop>} />
        <Route path="/relatorios/renovacao-pacotes" element={<ConfiguracaoSomenteDesktop titulo="Pacotes e guias a renovar"><RelatorioRenovacaoPacotesTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/relatorios/saldo-sem-agenda" element={<ConfiguracaoSomenteDesktop titulo="Saldo sem agenda"><RelatorioSaldoSemAgendaTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/relatorios/financeiro/descontos" element={<ConfiguracaoSomenteDesktop titulo="Descontos e acréscimos concedidos"><RelatorioFinanceiroTela tipo="descontos" /></ConfiguracaoSomenteDesktop>} />
        <Route path="/titulosPlano" element={<ConfiguracaoSomenteDesktop titulo="Títulos a faturar"><TitulosPlanoTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/importacoes" element={<ConfiguracaoSomenteDesktop titulo="Importar planilhas"><ImportacaoPlanilhasTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/parametros" element={<ConfiguracaoSomenteDesktop titulo="Parâmetros do sistema"><ParametrosSistemaTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/faturasPlano" element={<ConfiguracaoSomenteDesktop titulo="Faturas de planos de saúde"><FaturasPlanoTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/notasPlano" element={<ConfiguracaoSomenteDesktop titulo="Notas fiscais de planos"><NotasPlanoTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/motivosGlosa" element={<ConfiguracaoSomenteDesktop titulo="Motivos de glosa"><MotivosGlosaTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/contasReceber" element={<ConfiguracaoSomenteDesktop titulo="Contas a receber"><ContasReceberTela /></ConfiguracaoSomenteDesktop>} />
        <Route path="/usuarios" element={<UsuariosPerfisTela />} />
        <Route path="/perfis" element={<PerfisPermissoesTela />} />
        <Route path="/sem-permissao" element={<SemPermissao />} />
        <Route path="/definir-senha" element={<DefinirSenha />} />
        <Route path="/minha-conta" element={<MinhaConta />} />
        <Route path="*" element={<h1>Erro 404 - Rota não encontrada</h1>} />
      </Routes>
      </Suspense>
      {/* F1 (F-D1): Hoje · Agenda · Pacientes · Mais no rodapé do celular (perfil profissional) */}
      <BarraNavegacaoInferior />
      </GuardaPerfil>
    </DominioProviders>
  );
}

function AppRoutes() {
  return (
    <>
      <AuthBoot />
      <AvisoSessaoTrocada />
      <AvisoConcorrencia />
      <AvisoCadastroIncompleto />
      <FaixaEmpresaTeste />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<PrivateRoute />}>
          <Route path="/*" element={<RotasAutenticadas />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  const [versaoTenant, setVersaoTenant] = useState(0);

  const limparEstadoTenant = useCallback(() => {
    // Incrementa versão para forçar remount da árvore de domínio
    setVersaoTenant((v) => v + 1);
  }, []);

  return (
    <SnackBarContextProvider>
      <AuthProvider onLimparEstadoTenant={limparEstadoTenant}>
        <BrowserRouter>
          <AppRoutes key={`app-${versaoTenant}`} />
        </BrowserRouter>
      </AuthProvider>
    </SnackBarContextProvider>
  );
}

export default App;
